import { useState } from 'react';
import Sheet from '../Sheet';
import { useNavigate } from 'react-router-dom';
import { BookingScreen } from '../liveBooking/BookingScreen';
import { requestLiveNewTime } from '../../services/liveV2';
import { createConversation } from '../../services/chat';
import { useAuth } from '../../contexts/AuthContext';
import { liveLessonsErrorMessage, moveLiveLesson } from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { bookingWindowFrom } from '../../utils/bookingWindow';
import { whenLine } from '../../utils/liveCalendar';
import { firstNameOf } from '../../utils/liveLessonText';
import { devAnyTimeStart } from '../../utils/lessonTimes';
import { useLiveConfig } from '../../hooks/useLiveConfig';
import { TOO_CLOSE_LINE, TOO_CLOSE_TITLE, tooCloseToMove } from '../../utils/moveRule';
import { PANE_CARD } from './surfaces';
import type { CoachAvailabilitySlot, LiveCallBooking, LiveOfferingType } from '../../types/liveCall';
import { MessageButton } from './MessageButton';
import { completeOptimistically } from '../../services/optimistic';
import { movedPatch } from '../../utils/optimisticLessons';

/**
 * Request a New Time (redesign spec, batch 4; one flow, Propose and
 * Reschedule are gone): the one booking screen with its header and button
 * changed, "This replaces Tue, Oct 20 at 1:00 PM" at the top and Send to
 * Justin. The lesson keeps its mode, so the screen does not ask it. The
 * other side gets one notification and accepts or declines on the card.
 * Opened by Reschedule, wherever a lesson moves (Ryan, 28 Oct), and titled
 * Request a New Time.
 * `banner` replaces the line where the flow starts from this month's
 * lessons, and `weekly` puts the member's usual time first.
 * Decision 123: for the player the lesson moves at once (moveLiveLesson)
 * and the coach is told; `proposalId` is the coach's proposal answered
 * with Pick something else. The request and its answer are the coach's.
 */
export function RequestNewTime({
  title, booking, isCoach, tz, otherName, otherPhoto, banner, weekly, proposalId, onClose,
}: {
  /** The screen's title: Request a New Time. */
  title: string;
  booking: LiveCallBooking;
  isCoach: boolean;
  tz: string;
  otherName: string;
  otherPhoto?: string | null;
  banner?: string;
  weekly?: { weekday: number; time: string } | null;
  /** The coach's proposal the player answered with Pick something else. */
  proposalId?: string;
  onClose: () => void;
}) {
  const { showError } = useToast();
  // Decision 125: the player's move sheet is Pick a New Time; the coach's keeps its title.
  const shown = isCoach ? title : 'Pick a New Time';
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [slot, setSlot] = useState<CoachAvailabilitySlot | null>(null);
  const [sending, setSending] = useState(false);
  const [zone, setZone] = useState(tz);
  const other = firstNameOf(otherName, isCoach ? 'your player' : 'your coach');
  const identity = useUserIdentity(isCoach ? booking.playerId : booking.coachId, isCoach ? 'player' : 'coach');
  // The 10-hour move rule (section 6): inside it the sheet says so, never vanishes; DEV ONLY: LIVE_DEV_ANY_TIME lifts it.
  const config = useLiveConfig();
  const [openedAt] = useState(() => Date.now());
  const tooClose = !isCoach && !!config && tooCloseToMove({ startAt: booking.scheduledStartAt, nowMs: openedAt, devAnyTime: config.devAnyTime });

  const send = async () => {
    if (!slot) return;
    // DEV ONLY: LIVE_DEV_ANY_TIME. A drawn time is sent as its start.
    const startAt = devAnyTimeStart(slot);
    const to = startAt ? { bookingId: booking.id, toStartAt: startAt } : { bookingId: booking.id, toSlotId: slot.id };
    if (!isCoach) {
      // Set New Time completes on the device (speed.md 2, decision 157): the lesson page with the new time and
      // "Moved to ..." at once, moveLiveLesson behind it with one requestId, a toast only if every try fails.
      // It replaces decision 125's success toast: the finished screen says it.
      completeOptimistically(booking.id, movedPatch(booking, slot.startAt, zone),
        (requestId) => moveLiveLesson({ ...to, ...(proposalId && { proposalId }), requestId }),
        { keepLines: true, onFail: (err) => showError(liveLessonsErrorMessage(err, "Couldn't set the new time. Try again.")) });
      onClose();
      return;
    }
    setSending(true);
    try {
      await requestLiveNewTime(to);
      onClose();
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not send your request. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  // No open times: Message {coach} opens their thread.
  const messageOther = async () => {
    if (!currentUser) return;
    try {
      navigate(`/inbox/${await createConversation(currentUser.uid, isCoach ? booking.playerId : booking.coachId)}`);
    } catch {
      showError(`Could not open your messages with ${other}. Please try again.`);
    }
  };

  if (tooClose) {
    return (
      <Sheet title={shown} onClose={onClose} dismissLabel="Close">
        <div className={`${PANE_CARD} mb-2 mt-2`} data-too-close>
          <p className="text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">{TOO_CLOSE_TITLE}</p>
          <p className="mt-1 text-[15px] leading-[20px] text-db-text-2">{TOO_CLOSE_LINE}</p>
          <div className="mt-3 flex"><MessageButton name={other} withName onClick={() => { void messageOther(); }} /></div>
        </div>
      </Sheet>
    );
  }

  // The sheet (components.md 10): Cancel and the title in its bar, the booking calendar inside.
  return (
    <Sheet title={shown} onClose={onClose} busy={sending}>
      <BookingScreen
        coachId={booking.coachId}
        offeringType={booking.offeringType as LiveOfferingType}
        coachName={otherName}
        coachFirstName={other}
        photoUrl={otherPhoto ?? identity?.avatarUrl}
        title={shown}
        line={isCoach ? `Pick any open time. ${other} gets one notification and can accept or decline.` : `From ${other}'s open times. It changes right away and ${other} is told.`}
        banner={(
          <p className="rounded-[26px] bg-white px-4 py-3.5 text-[15px] leading-[20px] text-db-text-1">
            {banner ?? `This replaces ${whenLine(booking.scheduledStartAt, zone)}`}
          </p>
        )}
        latestStartAt={bookingWindowFrom(new Date()).latestStartAt}
        timeZone={zone}
        onZoneChange={setZone}
        weekly={weekly}
        selected={slot}
        onSelect={setSlot}
        buttonLabel={isCoach ? `Send to ${other}` : 'Set New Time'}
        busy={sending}
        onSubmit={() => { void send(); }}
        onBack={onClose}
        onMessage={messageOther}
        ownTimes={isCoach}
        devAnyTime
        inSheet
      />
    </Sheet>
  );
}
